import React, { useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';
import { cn } from '../../lib/utils';
import { useIsMobile } from '../../hooks/useMediaQuery';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  mobileFullScreen?: boolean;
}

interface ModalHeaderProps {
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
}

interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
}

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  full: 'max-w-none mx-4',
};

export function Modal({
  isOpen,
  onClose,
  children,
  className,
  size = 'md',
  mobileFullScreen = true,
}: ModalProps) {
  const isMobile = useIsMobile();

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent background scrolling
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Use full screen on mobile if mobileFullScreen is true
  const effectiveSize = isMobile && mobileFullScreen ? 'full' : size;
  const mobileClasses =
    isMobile && mobileFullScreen ? 'h-full max-h-none rounded-none' : 'max-h-[90vh]';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-30 z-40 modal-backdrop-enter"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div
          className={cn(
            'flex min-h-full items-center justify-center',
            isMobile && mobileFullScreen ? '' : 'p-4'
          )}
        >
          <div
            className={cn(
              'relative transform overflow-hidden bg-card text-left shadow-xl w-full overflow-y-auto border border-border modal-content-enter',
              isMobile && mobileFullScreen ? 'rounded-none' : 'rounded-lg',
              mobileClasses,
              sizeClasses[effectiveSize],
              className
            )}
            onClick={e => e.stopPropagation()}
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );
}

export function ModalHeader({ children, onClose, className }: ModalHeaderProps) {
  const isMobile = useIsMobile();

  return (
    <div className={cn('flex items-center justify-between p-6 pb-4', className)}>
      <div className="flex-1">{children}</div>
      {onClose && (
        <Button
          variant="ghost"
          onClick={onClose}
          className={cn(
            'rounded-md text-muted-foreground hover:text-foreground ml-4',
            isMobile && 'min-h-[44px] min-w-[44px] p-2'
          )}
        >
          <span className="sr-only">Close</span>
          <XMarkIcon className="h-6 w-6" />
        </Button>
      )}
    </div>
  );
}

export function ModalBody({ children, className }: ModalBodyProps) {
  return <div className={cn('px-6 py-4', className)}>{children}</div>;
}

export function ModalFooter({ children, className }: ModalFooterProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-3 px-6 py-4 pt-6 border-t border-border',
        className
      )}
    >
      {children}
    </div>
  );
}
